import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/Buttons";

type Props = {
  open: boolean;
  title: string;
  message?: string;
  details?: { label: string; value: string }[];
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  icon?: React.ReactNode;
  detailsLink?: { label: string; href: string };
  onConfirm: () => void;
  onClose: () => void;
  isBusy?: boolean;
  disableEnterConfirm?: boolean; // when true, Enter key won't confirm
  disableConfirmButton?: boolean; // when true, confirm button is disabled
  "data-testid"?: string;
};

export default function ConfirmDialog({ open, title, message, details = [], confirmLabel = "OK", cancelLabel = "Cancel", tone = "default", icon, detailsLink, onConfirm, onClose, isBusy, disableEnterConfirm = false, disableConfirmButton = false, "data-testid": dataTestId }: Props) {

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[10000]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] max-w-[95vw] rounded-xl border border-border-subtle bg-bg-elevated p-4 shadow-xl outline-none z-[10001]"
          data-testid={dataTestId}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose(); }
            if (e.key === 'Enter' && !disableEnterConfirm) { e.preventDefault(); if (!isBusy) onConfirm(); }
          }}
        >
          <div className="flex items-start gap-3">
            {icon && <div className="mt-0.5">{icon}</div>}
            <div className="flex-1">
              <Dialog.Title className="text-base font-semibold text-text">{title}</Dialog.Title>
              {message && <div className="text-sm text-text-dim mt-2 whitespace-pre-line">{message}</div>}
            </div>
          </div>
          {details.length > 0 && (
            <div className="mt-3 border border-border-subtle rounded-lg bg-bg p-3 text-sm">
              <dl className="space-y-1">
                {details.map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <dt className="text-text-dim">{d.label}</dt>
                    <dd className="ml-4 font-medium text-text break-all text-right">{d.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {detailsLink && (
            <div className="mt-3 text-xs">
              <a className="underline text-text-dim hover:text-text" href={detailsLink.href} target="_blank" rel="noreferrer">{detailsLink.label}</a>
            </div>
          )}
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button 
              variant="secondary" 
              onClick={onClose} 
              disabled={isBusy}
            >
              {cancelLabel}
            </Button>
            <Button 
              variant={tone === "danger" ? "danger" : "primary"}
              onClick={onConfirm} 
              disabled={isBusy || disableConfirmButton}
            >
              {isBusy ? 'Working…' : confirmLabel}
            </Button>
          </div>
          <div className="mt-1 text-[11px] text-text-dim text-right">{(disableEnterConfirm || disableConfirmButton) ? 'Esc to cancel' : 'Enter to confirm · Esc to cancel'}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}


